import Heading from "components/Typography/Heading"
import Input from "components/EvtKit/input/Input"
import SwitchV2 from "components/EvtKit/switch/SwitchV2"
import TextArea from "components/EvtKit/text-area/TextArea"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import { PricingUnits, tabIds } from "pages/purchase-plans/constants/defaultValues"
import IconArrowRight from "components/EvtKit/icons/IconArrowRight"
import { TabsContext } from "components/EvtKit/tabs/Tabs"
import Button from "components/EvtKit/button/Button"
import { ButtonWithConfirmation } from "components/ButtonWithConfirmation"
import ApiProducts from "api/ApiProducts"
import Tooltip from "components/Tooltip"

import { memo, useCallback, useContext, useEffect, useState } from "react"
import styled from "styled-components"
import clsx from "clsx"
import { useNavigate } from "react-router-dom"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  gap: 48px;
`

const EnterpriseSwitchContainer = styled.div`
  display: flex;
  position: relative;
`

const InlineContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
  align-items: center;
`

const InputContainer = styled.div`
  width: 100%;
`

const InputDescriptionContainer = styled.div`
  width: 100%;
  height: 65px;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  padding: 16px 0px 32px 0px;
`

interface PurchasePlanFormProps {
  data: App.IPurchasePlanForm
  handlePurchasePlanFormChange: (key: keyof App.IPurchasePlanForm, value: string | boolean) => void
  isValidPurchasePlanForm: boolean
  headingText: string
  onProductChange: (productId: string) => void
  children: React.ReactNode
  pricingUnit?: PricingUnits
  isUpdate?: boolean
  isDevMode?: boolean
}

const PurchasePlanForm = (props: PurchasePlanFormProps) => {
  const {
    data,
    handlePurchasePlanFormChange,
    isValidPurchasePlanForm,
    headingText,
    onProductChange,
    children,
    pricingUnit,
    isUpdate = false,
    isDevMode
  } = props

  const [productLabel, setProductLabel] = useState<string>("")
  const [error, setError] = useState<string>("")

  const onProductSelectorChange = useCallback((option: Option | null) => {
    const productId = option?.value || ""

    handlePurchasePlanFormChange("product", productId)

    setProductLabel(option?.label || "")
    onProductChange(productId)
  }, [])

  const onInputChange = useCallback(
    (key: "planKey" | "name" | "description") => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (key === "description" && e.target.value.length > 0 && e.target.value.length > 250) {
        setError("A descrição deve ter no máximo 250 caracteres")
      } else {
        setError("")
      }

      handlePurchasePlanFormChange(key, e.target.value)
    },
    []
  )

  const onIsEnterpriseChange = useCallback(() => {
    handlePurchasePlanFormChange("isEnterprise", !data.isEnterprise)
  }, [data.isEnterprise])

  const { onActiveTabIdChange } = useContext(TabsContext)

  const activateBenefitsTab = useCallback(() => {
    onActiveTabIdChange(tabIds.benefits)
  }, [onActiveTabIdChange])

  const getDataToProducts = useCallback(async () => {
    const results = await new ApiProducts().getProductOptions()

    return {
      hasMore: false,
      results
    }
  }, [])

  useEffect(() => {
    if (data.productLabel) {
      setProductLabel(data.productLabel)
    }
  }, [data.productLabel])

  const isEnterpriseSwitchValue = pricingUnit === PricingUnits.MONTHLY && !isDevMode ? false : data.isEnterprise

  const navigate = useNavigate()

  const navigateToPurchasePlanPage = useCallback(() => {
    navigate("/purchase-plans")
  }, [])

  return (
    <Container>
      <Heading variant="h6">{headingText}</Heading>

      <FormContainer>
        <InlineContainer>
          <EnterpriseSwitchContainer>
            {pricingUnit === PricingUnits.MONTHLY && !isDevMode && (
              <Tooltip
                text="No formato de cobrança mensal, o plano não pode ser enterprise."
                toolTipClassName="position-absolute"
                hoverClassName="positon-unset"
              >
                <SwitchV2
                  label="Plano Enterprise (somente)"
                  variant="medium"
                  onChange={onIsEnterpriseChange}
                  value={isEnterpriseSwitchValue}
                  grayBackground
                  disabled
                />
              </Tooltip>
            )}

            {(pricingUnit !== PricingUnits.MONTHLY || isDevMode) && (
              <SwitchV2
                label="Plano Enterprise (somente)"
                variant="medium"
                onChange={onIsEnterpriseChange}
                value={isEnterpriseSwitchValue}
                grayBackground
                disabled={isUpdate}
              />
            )}
          </EnterpriseSwitchContainer>

          <InputContainer>
            <Tooltip
              toolTipClassName="medium"
              text="A chave do plano é um identificador imutável para o plano. Esta informação é para uso interno e não ficará disponível para os usuários do produto."
            >
              <Input
                className="fullWidth"
                label="Chave do plano *"
                name="planKey"
                onChange={onInputChange("planKey")}
                placeholder="Digite"
                shrink
                value={data.planKey}
                disabled={isUpdate}
              />
            </Tooltip>
          </InputContainer>
        </InlineContainer>

        <InlineContainer>
          <InputContainer>
            <Input
              className="fullWidth"
              label="Nome do plano *"
              name="planName"
              onChange={onInputChange("name")}
              placeholder="Digite"
              shrink
              value={data.name}
            />
          </InputContainer>

          <SelectV2
            id="product"
            label="O plano refere-se a qual produto? *"
            onChange={onProductSelectorChange}
            placeholder="Selecione"
            shrink
            getData={getDataToProducts}
            value={productLabel}
            disabled={isUpdate}
          />
        </InlineContainer>

        <InputDescriptionContainer>
          <TextArea
            className="fullWidth fullHeight"
            label="Crie uma breve descrição sobre o plano *"
            name="description"
            onChange={onInputChange("description")}
            placeholder="Digite"
            shrink
            value={data.description}
            error={error}
            iconError
          />
        </InputDescriptionContainer>

        {children}
      </FormContainer>

      <ButtonContainer>
        <ButtonWithConfirmation
          label="Cancelar"
          className="fill-width secondary"
          actionWithConfirmation={navigateToPurchasePlanPage}
          actionHeading="Cancelar"
          actionText="Você tem alterações não salvas que serão perdidas ao sair."
        />
        <Button
          className={clsx("fill-width", { disabled: !isValidPurchasePlanForm })}
          disabled={!isValidPurchasePlanForm || error !== ""}
          iconRight={<IconArrowRight />}
          label="Avançar etapa"
          onClick={activateBenefitsTab}
        />
      </ButtonContainer>
    </Container>
  )
}

export default memo(PurchasePlanForm)
